/**
 * EcosystemContentPage: fetches and renders authored content for a tenant
 * page, falling back to a provided component when no content is published.
 *
 * P5 wave 2 (content renderer). Wired into App.js for the `landing` (home),
 * `about`, and `mission` routes on both apex and tenant, and additionally for
 * the `contact` route in tenant mode only (apex `/contact` stays hardcoded,
 * since the apex has no tenant admin to author it). The fetch targets the
 * public GET /api/ecosystem-content/:pageKey endpoint via the tenant-aware
 * contentApi service.
 *
 * Resolution:
 *   - loading        -> a centered Spinner.
 *   - published with non-empty blocks -> <EcosystemContentRenderer/>.
 *   - not published / empty blocks    -> the `fallback` prop.
 *   - fetch error    -> the `fallback` prop (a broken page is never shown;
 *                       the hardcoded fallback stays usable).
 *
 * Non-destructive design: a hardcoded page is passed as `fallback`, so a
 * surface whose content has not yet been authored keeps a usable page, and
 * authored content cleanly takes over once published.
 *
 * Apex vs. tenant: this component itself is surface-agnostic. The public
 * GET /api/ecosystem-content/:pageKey endpoint resolves the content ecosystem
 * server-side: the tenant in tenant mode, or the platform pseudo-Ecosystem
 * on the apex (P7 wave 1). App.js supplies the correct apex-vs-tenant
 * `fallback` for `landing`/`about`/`mission`; for `contact`, App.js only
 * mounts this component in tenant mode and renders the hardcoded ContactUs
 * directly on the apex, so no apex-authored contact content can ever surface.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Spinner } from 'react-bootstrap';

import { useContentApi } from '@/shared/services/contentApi';

import EcosystemContentRenderer from './EcosystemContentRenderer';

export default function EcosystemContentPage({ pageKey, fallback }) {
    const contentApi = useContentApi();

    // null = still resolving; { blocks, published } once resolved; on error
    // we resolve to a not-published shape so the fallback renders.
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);

        const load = async () => {
            try {
                const data = await contentApi.fetchPublishedContent(pageKey);
                if (cancelled) return;
                setContent(data);
            } catch {
                // Fetch failure: resolve to a not-published shape so the
                // fallback renders rather than a broken page. Errors are
                // non-fatal: the hardcoded fallback page is always usable.
                if (cancelled) return;
                setContent({ pageKey, blocks: [], published: false });
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        load();

        return () => {
            cancelled = true;
        };
    }, [contentApi, pageKey]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5 my-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading…</span>
                </Spinner>
            </div>
        );
    }

    // Render authored content only when the server reports it published AND
    // there are blocks to render. The `published` flag is the server's
    // authoritative signal (see the controller's getPublishedContent).
    const hasPublishedContent =
        content &&
        content.published === true &&
        Array.isArray(content.blocks) &&
        content.blocks.length > 0;

    if (hasPublishedContent) {
        return <EcosystemContentRenderer blocks={content.blocks} />;
    }

    // No published content: render the fallback (hardcoded page / empty
    // state).
    return fallback;
}

EcosystemContentPage.propTypes = {
    // One of 'landing' | 'about' | 'mission' | 'contact' (the server CONTENT_PAGE_KEYS).
    pageKey: PropTypes.oneOf(['landing', 'about', 'mission', 'contact'])
        .isRequired,
    // Rendered when no content is published, or on fetch error.
    fallback: PropTypes.node.isRequired,
};
